import { getSafe, isDefined, useDictionary, useLoadableState } from '@eih-netbroker/common';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { ReactComponent as DownloadIcon } from '../../../../assets/icons/download.svg';
import { ReactComponent as KIDIcon } from '../../../../assets/icons/kid.svg';
import { useLocale } from '../../../../context/NbLocalizationContext';
import { NbButton } from '../../../../shared/components/button/Button';
import { NbTooltip } from '../../../../shared/components/tooltip/Tooltip';
import { useAccounts } from '../../../../shared/services/useAccounts';
import { featureFlagUtils } from '../../../../shared/utils/FeatureFlagUtils';
import { useCreateNormalOrder, ValidatedOrder } from '../../services/order/useNormalOrderView';
import { useOrderConfirmCalculateCost } from '../../services/order/useOrderConfirmCalculateCost';
import { orderRows, useOrderRows } from '../../services/order/useOrderRows';
import { useOrderState } from '../OrderStateContext';
import classes from './OrderConfirm.module.scss';
import { OrderConfirmCalculatedCostView } from './OrderConfirmCalculatedCostView';
import { OrderExchangeConfirmView } from './OrderExchangeConfirmView';

interface OrderConfirmProps {
  orderForm: ValidatedOrder;
  isWithCurrencyExchange?: boolean;
}

export const OrderConfirm = ({ orderForm, isWithCurrencyExchange }: OrderConfirmProps) => {
  const { getOrderRows } = useOrderRows();
  const { translation } = useLocale();

  const { selectedAccountOrClient } = useAccounts();
  const { calculateOrderCost, resetCostCalculation } = useOrderConfirmCalculateCost();
  const { downloadPdfFileForNormalOrder, pdfDownloading } = useCreateNormalOrder();
  const { getOrderPrice } = useOrderState();
  const { formatDictionaryType } = useDictionary();

  const account = useMemo(
    () =>
      getSafe(
        selectedAccountOrClient.account,
        `A client's all account is selected, but you have to select only one account to place an order.`,
      ),
    [selectedAccountOrClient],
  );

  useEffect(() => {
    if (orderForm && orderForm.instrument) {
      calculateOrderCost(orderForm.instrument, orderForm);
    } else {
      resetCostCalculation();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on orderForm change
  }, [orderForm]);

  const price = useLoadableState(async () => {
    return await getOrderPrice({ ...orderForm, instrument: orderForm.instrument! });
  }, [orderForm, getOrderPrice]);

  const order = useMemo(() => {
    return {
      ...orderForm,
      accountName: `${account.clientId} - ${formatDictionaryType(account.accountCategory, 'accountCategoryTypes')}`,
      price: price.isLoaded ? price.value : undefined,
    };
  }, [orderForm, account, price, formatDictionaryType]);

  const rows = isDefined(order) ? orderRows.map(row => <Fragment key={row}>{getOrderRows(order, row)}</Fragment>) : [];

  const handleOnExportClick = useCallback(() => {
    downloadPdfFileForNormalOrder(order);
  }, [order, downloadPdfFileForNormalOrder]);

  return (
    <div className={classes.orderConfirm}>
      {rows}
      <OrderConfirmCalculatedCostView order={orderForm} />
      {featureFlagUtils.isOrderCurrencyExchangeEnabled && isWithCurrencyExchange && (
        <OrderExchangeConfirmView order={order} />
      )}

      <div className={classes.orderConfirmButtons}>
        <NbButton
          onClick={handleOnExportClick}
          data-testid="order-confirm-preliminary-expenses-button"
          isLoading={pdfDownloading}
          className={classes.orderConfirmLink}
          variant="text"
          size="small"
          endIcon={<DownloadIcon />}
        >
          {translation.orderConfirm.preliminaryExpenses}
        </NbButton>
        <div className={classes.kid}>
          <NbTooltip
            title={translation.general.KIDTooltip}
            placement={'top'}
          >
            <a
              data-testid="order-confirm-kid-document-link"
              href="https://kid.ersteinvestment.hu/"
              target={'_blank'}
              rel="noopener noreferrer"
            >
              {translation.general.KIDDocumentSearch} <KIDIcon />
            </a>
          </NbTooltip>
        </div>
      </div>
    </div>
  );
};
